import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Calendar, Clock, CheckCircle, Building2, User, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { apiGetPublicSchedulingPage, apiBookInterviewSlot } from "@/lib/api";

function fmtDate(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: tz,
  });
}

function fmtTime(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: tz });
}

function tzLabel(tz: string): string {
  try {
    // Get a short display name like "CET", "GMT+2", etc.
    return new Intl.DateTimeFormat("en-GB", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    return tz;
  }
}

function groupByDate(slots: Array<{ id: number; startsAt: string; endsAt: string }>) {
  const map: Record<string, typeof slots> = {};
  for (const s of slots) {
    const d = s.startsAt.split("T")[0];
    if (!map[d]) map[d] = [];
    map[d].push(s);
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
}

export default function InterviewSchedule() {
  const { tenantSlug, token } = useParams<{ tenantSlug: string; token: string }>();
  const [selectedSlot, setSelectedSlot] = useState<{ id: number; startsAt: string; endsAt: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bookedSlot, setBookedSlot] = useState<{ startsAt: string; endsAt: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-scheduling", tenantSlug, token],
    queryFn: () => apiGetPublicSchedulingPage(tenantSlug!, token!),
    enabled: !!tenantSlug && !!token,
    retry: false,
  });

  const bookMutation = useMutation({
    mutationFn: () => apiBookInterviewSlot(tenantSlug!, token!, selectedSlot!.id),
    onSuccess: (result) => {
      setBookedSlot(result.confirmedSlot);
      setConfirmOpen(false);
    },
  });

  const grouped = useMemo(() => groupByDate(data?.slots ?? []), [data?.slots]);
  const tz = data?.interview?.timezone ?? "UTC";
  const tzShort = tzLabel(tz);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Link Invalid or Expired</h2>
            <p className="text-sm text-muted-foreground">
              {(error as Error)?.message || "This scheduling link is no longer valid. Please contact the hiring team for a new link."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Already booked ─────────────────────────────────────────────────────────
  if (data.alreadyBooked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold mb-2">Interview Already Scheduled</h2>
            <p className="text-sm text-muted-foreground">Your interview is already confirmed.</p>
            {data.scheduledAt && (
              <p className="mt-3 text-sm font-medium text-violet-700">
                {fmtDate(data.scheduledAt, tz)} at {fmtTime(data.scheduledAt, tz)} {tzShort}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Booking confirmed ──────────────────────────────────────────────────────
  if (bookedSlot) {
    const icsContent = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//BestHR//Interview//EN",
      "BEGIN:VEVENT",
      `UID:${Date.now()}@besthr.app`,
      `DTSTAMP:${new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15)}Z`,
      `DTSTART:${new Date(bookedSlot.startsAt).toISOString().replace(/[-:.]/g, "").slice(0, 15)}Z`,
      `DTEND:${new Date(bookedSlot.endsAt).toISOString().replace(/[-:.]/g, "").slice(0, 15)}Z`,
      `SUMMARY:Interview – ${data.interview?.positionTitle ?? "Interview"}`,
      `DESCRIPTION:Interview at ${data.interview?.companyName ?? ""}`,
      "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");

    const downloadIcs = () => {
      const blob = new Blob([icsContent], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "interview.ics"; a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-1">Interview Confirmed!</h2>
            <p className="text-sm text-muted-foreground mb-6">You'll receive a confirmation email shortly.</p>
            <div className="bg-violet-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Scheduled For</p>
              <p className="font-semibold">{fmtDate(bookedSlot.startsAt, tz)}</p>
              <p className="text-sm text-muted-foreground">{fmtTime(bookedSlot.startsAt, tz)} – {fmtTime(bookedSlot.endsAt, tz)} {tzShort}</p>
            </div>
            <Button onClick={downloadIcs} className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
              <Calendar className="h-4 w-4 mr-2" />
              Add to Calendar (.ics)
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { interview, slots = [] } = data;

  // ── Main scheduling page ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-4 py-10 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 mx-auto mb-4">
          <img src="/besthr.png" alt="BestHR" className="h-9 w-9 object-contain" />
        </div>
        <h1 className="text-2xl font-bold mb-1">Schedule Your Interview</h1>
        <p className="text-violet-200 text-sm">Select a time that works best for you</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Info card */}
        {interview && (
          <Card className="mb-6">
            <CardContent className="pt-5 pb-5">
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4 text-violet-500" />
                  <span className="font-medium text-foreground">{interview.companyName ?? "The Company"}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4 text-violet-500" />
                  <span>{interview.positionTitle}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4 text-violet-500" />
                  <span>{interview.slotDurationMinutes} min slots</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No slots */}
        {slots.length === 0 && (
          <Card>
            <CardContent className="pt-10 pb-10 text-center">
              <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="font-medium">No available slots</p>
              <p className="text-sm text-muted-foreground mt-1">The interviewer hasn't added availability yet. Please check back later.</p>
            </CardContent>
          </Card>
        )}

        {/* Slot groups */}
        {grouped.map(([date, daySlots]) => (
          <div key={date} className="mb-6">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5" />
              {fmtDate(daySlots[0].startsAt, tz)}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {daySlots.map((slot) => {
                const isSelected = selectedSlot?.id === slot.id;
                return (
                  <button
                    key={slot.id}
                    onClick={() => { setSelectedSlot(slot); setConfirmOpen(true); }}
                    className={`rounded-xl border-2 p-3 text-center transition-all duration-150 ${
                      isSelected
                        ? "border-violet-500 bg-violet-50 text-violet-700"
                        : "border-border bg-card hover:border-violet-300 hover:bg-violet-50/50"
                    }`}
                  >
                    <p className="font-semibold text-sm">{fmtTime(slot.startsAt, tz)}</p>
                    <p className="text-xs text-muted-foreground">– {fmtTime(slot.endsAt, tz)} {tzShort}</p>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Your Interview Slot</DialogTitle>
            <DialogDescription>
              Please confirm the selected time. You'll receive a confirmation email after booking.
            </DialogDescription>
          </DialogHeader>
          {selectedSlot && (
            <div className="bg-violet-50 rounded-xl p-4 my-2">
              <p className="font-semibold">{fmtDate(selectedSlot.startsAt, tz)}</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {fmtTime(selectedSlot.startsAt, tz)} – {fmtTime(selectedSlot.endsAt, tz)} {tzShort}
              </p>
            </div>
          )}
          {bookMutation.error && (
            <p className="text-sm text-destructive">{(bookMutation.error as Error).message}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={bookMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => bookMutation.mutate()}
              disabled={bookMutation.isPending}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white"
            >
              {bookMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
